/**
 * Registration API Service
 * Handles C2 entity registration via the registration API endpoint
 */

import { getRuntimeConfig } from '../config/runtime';
import { logger } from '../utils/logger';
import {
	UnknownRiskRatingError,
	parseUpperRiskRating,
	riskRatingUpperToEnhancedProfile,
} from '../utils/riskRating';
import type {
	EnhancedProfileLevel,
	RegistrationRequest,
	RegistrationResponse,
	WorkflowType
} from './registrationApi.types';

// Re-export types for backward compatibility
export type {
	EnhancedProfileLevel, EntityCodeType, FormVariant, RegistrationAction, RegistrationRequest, RegistrationResponse, WorkflowType
} from './registrationApi.types';

// ─── API Functions ───────────────────────────────────────────────────────────

/**
 * Get the registration API base URL from runtime config
 */
function getRegistrationApiUrl(): string {
	const config = getRuntimeConfig();
	const url = config?.VITE_REGISTRATION_API_URL;
	if (!url) {
		throw new Error('Registration API URL not configured. Please set VITE_REGISTRATION_API_URL in config.json');
	}
	return url;
}

/**
 * Register a new C2 user account
 * 
 * @param request - The registration request data
 * @returns Promise<RegistrationResponse> - The registration response with loginId
 * 
 * @example
 * const response = await registerC2User({
 *   firstName: "RAJAT",
 *   lastName: "PANDEY",
 *   email: "rajatpandey00@gmail.com",
 *   phone: "+61449243114",
 *   dateOfBirth: "19890910",
 *   legalEntityCodeType: "ABN",
 *   legalEntityCodeData: "54653124068",
 *   workflow: "SOLETRADER"
 * });
 * 
 * // Response:
 * // {
 * //   action: "SUCCESS",
 * //   loginId: "rajpan0847",
 * //   workflow: "SOLETRADER",
 * //   statusText: "Account created successfully. Please check your email for login credentials.",
 * //   formVariant: "ASIC_ENTITY"
 * // }
 */
export async function registerC2User(request: RegistrationRequest): Promise<RegistrationResponse> {
	const baseUrl = getRegistrationApiUrl();
	const url = `${baseUrl}/register-v2`;

	logger.debug('[registrationApi] Registering C2 user', {
		email: request.email,
		workflow: request.workflow,
		entityType: request.legalEntityCodeType
	});

	try {
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'application/json',
			},
			body: JSON.stringify({
				...request,
				enhancedProfile: String(request.enhancedProfile),
			}),
		});

		let data: any;
		const responseText = await response.text();
		try {
			data = JSON.parse(responseText);
		} catch {
			data = { statusText: responseText || `Server returned ${response.status}` };
		}

		if (!response.ok) {
			logger.error('[registrationApi] Registration failed', {
				status: response.status,
				data
			});
			return {
				action: 'FAILURE',
				statusText: data.statusText || data.errorMessage || data.message || `Registration failed with status ${response.status}`,
				errorCode: data.errorCode,
				errorMessage: data.errorMessage || data.message,
			};
		}

		logger.info('[registrationApi] Registration successful', {
			loginId: data.loginId,
			workflow: data.workflow
		});

		return data;
	} catch (error) {
		logger.error('[registrationApi] Registration request failed', { error });
		return {
			action: 'ERROR',
			statusText: error instanceof Error ? error.message : 'Network error occurred during registration',
			errorMessage: 'Failed to connect to registration service. Please try again.',
		};
	}
}

// ─── Utility Functions ───────────────────────────────────────────────────────

/**
 * Format a Date object to the required YYYYMMDD format
 */
export function formatDateOfBirth(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}${month}${day}`;
}

/**
 * Parse a YYYYMMDD string to a Date object
 */
export function parseDateOfBirth(dateStr: string): Date | null {
	if (dateStr.length !== 8) return null;
	const year = parseInt(dateStr.substring(0, 4), 10);
	const month = parseInt(dateStr.substring(4, 6), 10) - 1;
	const day = parseInt(dateStr.substring(6, 8), 10);
	return new Date(year, month, day);
}

/**
 * Format a phone number — strips non-digit characters (except +) and ensures + prefix.
 * If the number starts with 0, it's left as-is (caller should provide full international format).
 */
export function formatPhoneNumber(phone: string): string {
	// Remove all non-digit characters except +
	let cleaned = phone.replace(/[^\d+]/g, '');

	// Fix common mistake: +610xxx → +61xxx (strip the 0 after country code)
	if (cleaned.startsWith('+61') && cleaned.length > 3 && cleaned[3] === '0') {
		cleaned = '+61' + cleaned.substring(4);
	}

	// If already has +, return as-is
	if (cleaned.startsWith('+')) {
		return cleaned;
	}

	// Otherwise prefix with + (assume user entered country code without it)
	return '+' + cleaned;
}

/**
 * Clean ABN/ACN by removing spaces and dashes
 */
export function cleanEntityCode(code: string): string {
	return code.replace(/[\s-]/g, '');
}

/**
 * Validate ABN format (11 digits)
 */
export function isValidABN(abn: string): boolean {
	const cleaned = cleanEntityCode(abn);
	return /^\d{11}$/.test(cleaned);
}

/**
 * Validate ACN format (9 digits)
 */
export function isValidACN(acn: string): boolean {
	const cleaned = cleanEntityCode(acn);
	return /^\d{9}$/.test(cleaned);
}

/**
 * Map a risk-assessment C2TypeId to the registration WorkflowType.
 * individual → INDIVIDUAL, sole_trader → SOLETRADER,
 * body_corporate / trust / government → NAMEDINDIVIDUAL (named legal entity).
 */
export function c2EntityTypeToWorkflow(c2EntityType: string): WorkflowType {
	const t = c2EntityType.toLowerCase();
	if (t === 'sole_trader') return 'SOLETRADER';
	if (t === 'individual') return 'INDIVIDUAL';
	// body_corporate, trust, government → named legal entity
	return 'NAMEDINDIVIDUAL';
}

/**
 * Get workflow type based on entity information
 */
export function determineWorkflow(entityType: string): WorkflowType {
	const type = entityType.toUpperCase();
	if (type.includes('SOLE')) return 'SOLETRADER';
	if (type.includes('NAMED')) return 'NAMEDINDIVIDUAL';
	if (type.includes('VEHICLE')) return 'VEHICLE';
	return 'INDIVIDUAL'; // Default
}

/**
 * Map KYB API austracType (from ABN/ACN lookup) to frontend WorkflowType.
 * The backend further refines this (e.g. NAMEDINDIVIDUAL → BUSINESSENTITY_NOPRINCIPALS_NEEDSUBO).
 */
export function austracTypeToWorkflow(austracType: string | undefined): WorkflowType {
	if (!austracType) return 'NAMEDINDIVIDUAL';
	const t = austracType.toLowerCase();
	// This is called from an ABN/ACN lookup: a natural person holding an ABN is a
	// sole trader (the register frequently reports the type as just "Individual").
	// Map both sole-trader wording and bare "individual" → SOLETRADER so the ABN is
	// retained rather than downgrading to the INDIVIDUAL (no-ABN) workflow.
	if (t.includes('sole trader') || t === 'sole_trader' || t === 'individual') return 'SOLETRADER';
	// Company, Trust, Partnership, Association, Government, etc. → NAMEDINDIVIDUAL
	return 'NAMEDINDIVIDUAL';
}

/**
 * Map a risk rating string to the enhancedProfile level for registration.
 * High → 3 (enhanced due diligence), Medium → 2, Low → 1 (standard).
 */
/**
 * AL-442 #4: folded onto the shared mapper. Behaviour is unchanged.
 *
 * Its only caller passes `SavedAssessment.riskRating`, typed
 * RiskRatingUpper = 'HIGH' | 'MEDIUM' | 'LOW', so all three inputs match and
 * `1` is only ever reached by 'LOW' — a correct result rather than a fallback.
 * The 'HIGH RISK' / 'MED RISK' spellings it also accepted were unreachable from
 * that caller.
 *
 * The no-assessment case is handled by the caller and must stay as it is:
 * useInvitationsState defaults enhancedProfile to 3 (High) when there is no
 * assessment at all, so absence triggers enhanced due diligence. That is the
 * opposite of the defect on this ticket and is deliberate.
 */
export function riskRatingToEnhancedProfile(rating: string): EnhancedProfileLevel {
	const parsed = parseUpperRiskRating(rating);
	if (!parsed) {
		throw new UnknownRiskRatingError(rating, ['HIGH', 'MEDIUM', 'LOW']);
	}
	return riskRatingUpperToEnhancedProfile(parsed);
}

export default {
	registerC2User,
	formatDateOfBirth,
	parseDateOfBirth,
	formatPhoneNumber,
	cleanEntityCode,
	isValidABN,
	isValidACN,
	determineWorkflow,
	c2EntityTypeToWorkflow,
	austracTypeToWorkflow,
	riskRatingToEnhancedProfile,
};
